import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    TextField,
    useTheme,
    Grid,
    Paper,
    Chip,
    Divider,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import EmailIcon from "@mui/icons-material/Email";

import { styled, alpha, darken, lighten } from "@mui/material/styles";
import { DataGridPro, useGridApiContext } from "@mui/x-data-grid-pro";
import Header from "../../components/Header";
import MediaViewer from "../../components/mediaviewer";
import LogNotes from "../../components/LogNotes";
import ExpenseEntry from "../../component-files/Forms/ExpenseEntry";
import { tokens } from "../../theme";

/* ---- MUI X v6+ date pickers ---- */
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";

/* ---------------------------------- API ---------------------------------- */
const API_UPLOAD_INVOICES = "https://media.swancreate.com/upload";
const API_EXPENSES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Expenses";
const API_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_categories";
const API_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_subcategories";
const API_VENDORS = "https://figazuresql.swancreate.com/api/swa/FINANCE_vendors";
const API_EXPENSE_DETAILS = "https://figazuresql.swancreate.com/api/swa/FINANCE_ExpenseDetails";
const API_SCHOOL_MST = "https://figazuresql.swancreate.com/api/swa/SchoolMst";
const API_REIMBURSEMENT_PROGRAMS =
    "https://figazuresql.swancreate.com/api/swa/FINANCE_ReimbursementPrograms";
const API_AZ_INVOICE_ITEMS = "https://figazuresql.swancreate.com/api/swa/AZ_InvoiceItems";

/* ------------------------------- Utilities ------------------------------- */
const getCurrentSYStart = (today = new Date()) =>
    today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;

const inSchoolYear = (d, startYear) =>
    d >= new Date(startYear, 6, 1) && d < new Date(startYear + 1, 6, 1);

const formatSYLabel = (startYear) => `${startYear}–${startYear + 1}`;

const coerceBool = (v) =>
    v === true || v === 1 || v === "1" || v === "true" || v === "TRUE" || v === "Y" || v === "y";

/* --------------------------- Grid look-and-feel -------------------------- */
const StyledDataGrid = styled(DataGridPro)(({ theme }) => {
    const isDark = theme.palette.mode === "dark";
    const paper = theme.palette.background.paper;
    const headerBg = isDark ? darken(paper, 0.12) : lighten(paper, 0.04);
    const headerFg = theme.palette.getContrastText(headerBg);

    return {
        border: 0,
        backgroundColor: paper,
        color: theme.palette.text.primary,
        "--row-hover": alpha(theme.palette.action.hover, 0.6),

        "& .MuiDataGrid-columnHeaders": {
            backgroundColor: headerBg,
            color: headerFg,
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
        "& .MuiDataGrid-iconSeparator": { color: alpha(headerFg, 0.5) },
        "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },

        "& .row--odd": {
            backgroundColor: isDark
                ? alpha(theme.palette.primary.main, 0.06)
                : alpha(theme.palette.primary.main, 0.03),
        },

        "& .paidRow": {
            backgroundColor: alpha(theme.palette.success.main, 0.18) + " !important",
        },
        "& .varianceRow": {
            backgroundColor: alpha(theme.palette.error.main, 0.18) + " !important",
        },
    };
});

/* ------------------------------ Tooltips --------------------------------- */
const tooltipSx = (theme) => ({
    "& .MuiTooltip-tooltip": {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        boxShadow: theme.shadows[2],
        fontWeight: 600,
        fontSize: "0.9rem",
    },
});

/* --------------------------- Solid button style -------------------------- */
const solidButtonSx = (theme) => ({
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 1.2,
    boxShadow: theme.shadows[2],
    "&.MuiButton-containedPrimary": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        "&:hover": { backgroundColor: darken(theme.palette.primary.main, 0.12) },
    },
    "&.MuiButton-containedSecondary": {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.getContrastText(theme.palette.secondary.main),
        "&:hover": { backgroundColor: darken(theme.palette.secondary.main, 0.12) },
    },
});

/* ---------------------- Helpers for picker <-> ISO ----------------------- */
const toISO = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") {
        const d = val.toDate();
        return Number.isNaN(d.getTime()) ? null : d.toISOString();
    }
    const d = val instanceof Date ? val : new Date(val);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/* -------------------------- Edit cells: Date ----------------------------- */
function DateEditCell(props) {
    const { id, field, value } = props;
    const apiRef = useGridApiContext();
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
                value={value ? dayjs(value) : null}
                onChange={(newVal) => apiRef.current.setEditCellValue({ id, field, value: toISO(newVal) })}
                format="MM/DD/YYYY"
                slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
        </LocalizationProvider>
    );
}

function DateTimeEditCell(props) {
    const { id, field, value } = props;
    const apiRef = useGridApiContext();
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDateTimePicker
                value={value ? dayjs(value) : null}
                onChange={(newVal) => apiRef.current.setEditCellValue({ id, field, value: toISO(newVal) })}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
        </LocalizationProvider>
    );
}

/* =============================== Component =============================== */
const Expenses = () => {
    const fileInputRefs = useRef({});
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [expenseData, setExpenseData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [subcategoryData, setSubcategoryData] = useState([]);
    const [vendorData, setVendorData] = useState([]);
    const [schoolData, setSchoolData] = useState([]);
    const [reimbursementPrograms, setReimbursementPrograms] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [selectedSYStart, setSelectedSYStart] = useState(getCurrentSYStart());
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedSite, setSelectedSite] = useState("");
    const [selectedSubcategory, setSelectedSubcategory] = useState("");
    const [selectedVendor, setSelectedVendor] = useState("");
    const [selectedCategorized, setSelectedCategorized] = useState("");
    const [selectedIsVariance, setSelectedIsVariance] = useState("");
    const [selectedInvoice, setSelectedInvoice] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [invoiceInput, setInvoiceInput] = useState(""); // debounced input

    // Modals / details
    const [isExpenseEntryOpen, setIsExpenseEntryOpen] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [editingItemId, setEditingItemId] = useState(null);
    const [editingItemData, setEditingItemData] = useState({});

    // Notes
    const [logNotesOpen, setLogNotesOpen] = useState(false);
    const [selectedEventIdForLog, setSelectedEventIdForLog] = useState(null);
    const [notesMap, setNotesMap] = useState({});

    // Pricing variance
    const [varianceOpen, setVarianceOpen] = useState(false);
    const [varianceItems, setVarianceItems] = useState([]);
    const [varianceHeader, setVarianceHeader] = useState(null);
    const [varianceTotals, setVarianceTotals] = useState({ over: 0, under: 0, net: 0 });

    /* ------------------------- Init & Data Fetch --------------------------- */
    useEffect(() => {
        fetchCategories();
        fetchSubcategories();
        fetchVendors();
        fetchSchoolData();
        fetchReimbursementPrograms();
        fetchExpenses();
    }, []);

    useEffect(() => {
        const fetchNotesMap = async () => {
            try {
                const { data } = await axios.get(
                    "https://figazuresql.swancreate.com/api/swa/FINANCE_Log"
                );
                const map = {};
                (Array.isArray(data) ? data : []).forEach((log) => {
                    if (!map[log.eventid]) map[log.eventid] = [];
                    map[log.eventid].push(log);
                });
                setNotesMap(map);
            } catch { }
        };
        fetchNotesMap();
    }, []);

    // Keep subcategory valid when category changes
    useEffect(() => {
        if (!selectedCategory) return;
        const allowedSubs = subcategoryData
            .filter((sc) => String(sc.CategoryID) === String(selectedCategory))
            .map((sc) => sc.SubcategoryName);
        if (selectedSubcategory && !allowedSubs.includes(selectedSubcategory)) {
            setSelectedSubcategory("");
        }
    }, [selectedCategory, selectedSubcategory, subcategoryData]);

    // debounce the invoice text filter (cuts filter work on every keystroke)
    useEffect(() => {
        const t = setTimeout(() => setSelectedInvoice(invoiceInput), 250);
        return () => clearTimeout(t);
    }, [invoiceInput]);

    /* ------------------------- API calls ----------------------------------- */
    const fetchCategories = async () => {
        try {
            const res = await fetch(API_CATEGORIES);
            setCategoryData(await res.json());
        } catch {
            setError("Failed to load categories.");
        }
    };
    const fetchSubcategories = async () => {
        try {
            const res = await fetch(API_SUBCATEGORIES);
            setSubcategoryData(await res.json());
        } catch { }
    };
    const fetchVendors = async () => {
        try {
            const res = await fetch(API_VENDORS);
            setVendorData(await res.json());
        } catch {
            setError("Failed to load vendors.");
        }
    };
    const fetchSchoolData = async () => {
        try {
            const res = await fetch(API_SCHOOL_MST);
            const raw = await res.json();
            setSchoolData(raw.sort((a, b) => a.SchoolName.localeCompare(b.SchoolName)));
        } catch {
            setError("Failed to load school data.");
        }
    };
    const fetchReimbursementPrograms = async () => {
        try {
            const res = await fetch(API_REIMBURSEMENT_PROGRAMS);
            setReimbursementPrograms(await res.json());
        } catch {
            setError("Failed to load reimbursement programs.");
        }
    };
    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const res = await fetch(API_EXPENSES);
            const raw = await res.json();

            const normalized = (Array.isArray(raw) ? raw : []).map((r) => {
                const InvoiceNumber =
                    r.InvoiceNumber ??
                    r.invoice_number ??
                    r.invoiceNumber ??
                    r.Invoice ??
                    r.InvoiceNbr ??
                    r.invoice_no ??
                    r.invoiceNo ??
                    "";

                const isVariance = coerceBool(r.isVariance);
                const isPaid = coerceBool(r.isPaid);
                const isCategorized = coerceBool(r.isCategorized);
                const isInvoice = coerceBool(r.isInvoice) || Boolean(InvoiceNumber);

                const d = r.ExpenseDate ? new Date(r.ExpenseDate) : null;
                const ExpenseMonthName = d
                    ? d.toLocaleString("en-US", { month: "long" })
                    : "";

                return {
                    ...r,
                    InvoiceNumber,
                    isVariance,
                    isPaid,
                    isCategorized,
                    isInvoice,
                    ExpenseMonthName,
                    SiteName: r.SiteName ?? "",
                    PaymentType: r.PaymentType ?? "",
                    PaymentInformation: r.PaymentInformation ?? "",
                    ReimbursmentTrackingProgram: r.ReimbursmentTrackingProgram ?? "",
                };
            });

            setExpenseData(normalized);
        } catch {
            setError("Failed to load expenses.");
        } finally {
            setLoading(false);
        }
    };

    /* ------------------ Upload & inline updates ---------------------------- */
    const handleImageUpload = useCallback(async (expenseApiId, file) => {
        const ext = file.name.substring(file.name.lastIndexOf("."));
        const newFilename = `${uuidv4()}${ext}`;

        const formData = new FormData();
        formData.append("file", file, newFilename);
        formData.append("folder", "expenses");

        try {
            await axios.post(API_UPLOAD_INVOICES, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const imageURL = `https://media.swancreate.com/images/expenses/${newFilename}`;
            const imageName = newFilename;

            await axios.patch(
                `${API_EXPENSES}/${expenseApiId}`,
                { imageURL, imageName },
                { headers: { "Content-Type": "application/json" } }
            );

            setExpenseData((prev) =>
                prev.map((exp) => (exp.apiid === expenseApiId ? { ...exp, imageURL, imageName } : exp))
            );
        } catch (err) {
            console.error("[ImageUpload] ERROR:", err?.response || err?.message);
            setError("Failed to upload image.");
        }
    }, []);

    const handleDeleteClick = useCallback(async (id) => {
        try {
            await fetch(`${API_EXPENSES}/${id}`, {
                method: "DELETE",
                headers: { Accept: "application/json" },
            });
            setExpenseData((prev) => prev.filter((e) => e.apiid !== id));
        } catch {
            setError("Failed to delete expense.");
        }
    }, []);

    const handlePriceIssueChange = useCallback(async (id, newValue) => {
        try {
            await fetch(`${API_EXPENSES}/${id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ isVariance: newValue }),
            });
            setExpenseData((prev) =>
                prev.map((e) => (e.apiid === id ? { ...e, isVariance: newValue } : e))
            );
        } catch {
            setError("Failed to update Price Issue.");
        }
    }, []);

    const handleAddNote = useCallback((eventId) => {
        setSelectedEventIdForLog(eventId);
        setLogNotesOpen(true);
    }, []);

    const handleCellEditCommit = useCallback(async (params) => {
        const patchRow = async (payload) =>
            fetch(`${API_EXPENSES}/${params.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(payload),
            });

        try {
            switch (params.field) {
                case "isPaid": {
                    const paymentDate = params.value ? new Date().toISOString() : null;
                    await patchRow({ isPaid: params.value, PaymentDate: paymentDate });
                    break;
                }
                case "PaymentType": {
                    await patchRow({ PaymentType: params.value ?? "" });
                    break;
                }
                case "PaymentInformation": {
                    await patchRow({ PaymentInformation: params.value ?? "" });
                    break;
                }
                case "ExpenseDate": {
                    await patchRow({ ExpenseDate: params.value });
                    setExpenseData((prev) =>
                        prev.map((e) => (e.apiid === params.id ? { ...e, ExpenseDate: params.value } : e))
                    );
                    break;
                }
                case "PaymentDate": {
                    await patchRow({ PaymentDate: params.value });
                    break;
                }
                case "VendorName": {
                    const vendor = vendorData.find((v) => v.VendorName === params.value);
                    if (!vendor) throw new Error("Vendor not found");
                    await patchRow({ VendorName: params.value, VendorID: vendor.VendorID });
                    setExpenseData((prev) =>
                        prev.map((e) =>
                            e.apiid === params.id ? { ...e, VendorName: params.value, VendorID: vendor.VendorID } : e
                        )
                    );
                    break;
                }
                case "ExpenseCategory": {
                    const cat = categoryData.find((c) => c.CategoryName === params.value);
                    if (!cat) throw new Error("Category not found");
                    await patchRow({ ExpenseCategory: params.value, ExpenseCategoryID: cat.CategoryID });
                    setExpenseData((prev) =>
                        prev.map((e) =>
                            e.apiid === params.id
                                ? { ...e, ExpenseCategory: params.value, ExpenseCategoryID: cat.CategoryID }
                                : e
                        )
                    );
                    break;
                }
                case "ExpenseSubcategory": {
                    const sc = subcategoryData.find((s) => s.SubcategoryName === params.value);
                    if (!sc) throw new Error("Subcategory not found");
                    await patchRow({ ExpenseSubcategory: params.value, ExpenseSubCategoryID: sc.SubcategoryID });
                    setExpenseData((prev) =>
                        prev.map((e) =>
                            e.apiid === params.id
                                ? { ...e, ExpenseSubcategory: params.value, ExpenseSubCategoryID: sc.SubcategoryID }
                                : e
                        )
                    );
                    break;
                }
                case "Amount": {
                    const num = Number(params.value ?? 0);
                    await patchRow({ Amount: num });
                    break;
                }
                case "ReimbursmentTrackingProgram": {
                    const programObj = reimbursementPrograms.find(
                        (p) => (p.program_abbreviation || "NONE") === params.value
                    );
                    if (!programObj) throw new Error("Program not found");
                    await patchRow({
                        ReimbursmentTrackingProgram: params.value,
                        ReimbursementTrackingId: programObj.programid,
                    });
                    setExpenseData((prev) =>
                        prev.map((e) =>
                            e.apiid === params.id
                                ? {
                                    ...e,
                                    ReimbursmentTrackingProgram: params.value,
                                    ReimbursementTrackingId: programObj.programid,
                                }
                                : e
                        )
                    );
                    break;
                }
                case "SiteName": {
                    const schoolObj = schoolData.find((s) => s.SchoolName === params.value);
                    if (!schoolObj) throw new Error("School not found");
                    await patchRow({ SchoolId: schoolObj.SchoolId, SiteName: params.value });
                    setExpenseData((prev) =>
                        prev.map((e) =>
                            e.apiid === params.id
                                ? { ...e, SchoolId: schoolObj.SchoolId, SiteName: params.value }
                                : e
                        )
                    );
                    break;
                }
                case "Description": {
                    await patchRow({ Description: params.value ?? "" });
                    break;
                }
                default:
                    break;
            }
        } catch (err) {
            console.error("Edit error:", err);
            setError("Failed to update. Please try again.");
        }
    }, [categoryData, reimbursementPrograms, schoolData, vendorData]);

    /* ------------------------- Options + Months ---------------------------- */
    const vendorsList = useMemo(
        () => vendorData.map((v) => v.VendorName).sort((a, b) => a.localeCompare(b)),
        [vendorData]
    );

    const months = useMemo(
        () => [...new Set(expenseData.filter(e => e.ExpenseMonthName).map(e => e.ExpenseMonthName))],
        [expenseData]
    );

    const availableSYStarts = useMemo(() => {
        const starts = new Set();
        expenseData.forEach((e) => {
            if (!e.ExpenseDate) return;
            const d = new Date(e.ExpenseDate);
            const syStart = getCurrentSYStart(d);
            starts.add(syStart);
        });
        if (starts.size === 0) {
            const cur = getCurrentSYStart();
            return [cur + 1, cur, cur - 1];
        }
        return Array.from(starts).sort((a, b) => b - a);
    }, [expenseData]);

    /* -------------------- Group: one row per invoice ---------------------- */
    const groupedByInvoice = useMemo(() => {
        const map = new Map();
        const noInvRows = [];
        const norm = (s) => (s || "").toString().trim().toUpperCase();

        for (const r of expenseData) {
            const inv = norm(r.InvoiceNumber);
            if (!inv) {
                noInvRows.push({ ...r, apiid: r.apiid });
                continue;
            }
            if (!map.has(inv)) {
                map.set(inv, {
                    ...r,
                    Amount: Number(r.Amount || 0),
                    isVariance: !!r.isVariance,
                    isPaid: !!r.isPaid,
                    apiid: r.apiid,
                    __groupCount: 1,
                });
            } else {
                const g = map.get(inv);
                g.Amount = Number(g.Amount || 0) + Number(r.Amount || 0);
                g.isVariance = g.isVariance || !!r.isVariance;
                g.isPaid = g.isPaid || !!r.isPaid;
                g.__groupCount += 1;
                if (!g.SiteName && r.SiteName) g.SiteName = r.SiteName;
                if (!g.VendorName && r.VendorName) g.VendorName = r.VendorName;
                if (!g.ExpenseDate && r.ExpenseDate) g.ExpenseDate = r.ExpenseDate;
                if (!g.ReimbursmentTrackingProgram && r.ReimbursmentTrackingProgram)
                    g.ReimbursmentTrackingProgram = r.ReimbursmentTrackingProgram;
                if (!g.PaymentType && r.PaymentType) g.PaymentType = r.PaymentType;
                if (!g.PaymentInformation && r.PaymentInformation) g.PaymentInformation = r.PaymentInformation;
                if (r.isInvoice && !g.isInvoice) {
                    g.isInvoice = true;
                    g.apiid = r.apiid;
                }
            }
        }

        return [...map.values(), ...noInvRows];
    }, [expenseData]);

    /* ------------------------- Filtering (fast) ---------------------------- */
    const filteredExpenses = useMemo(() => {
        const start = selectedSYStart ?? getCurrentSYStart();

        return groupedByInvoice.filter((expense) => {
            if (!expense.ExpenseDate) return false;

            const dt = new Date(expense.ExpenseDate);
            if (!inSchoolYear(dt, start)) return false;

            const matchesMonth = !selectedMonth || expense.ExpenseMonthName === selectedMonth;

            const matchesCategory =
                !selectedCategory ||
                String(expense.ExpenseCategoryID) === String(selectedCategory) ||
                categoryData.find((c) => String(c.CategoryID) === String(selectedCategory))?.CategoryName ===
                expense.ExpenseCategory;

            const matchesSite = !selectedSite || expense.SiteName === selectedSite;
            const matchesSubcategory =
                !selectedSubcategory || expense.ExpenseSubcategory === selectedSubcategory;
            const matchesVendor = !selectedVendor || expense.VendorName === selectedVendor;
            const matchesCategorized =
                !selectedCategorized ||
                (selectedCategorized === "categorized" ? expense.isCategorized : !expense.isCategorized);
            const matchesVariance =
                !selectedIsVariance || (selectedIsVariance === "review" ? expense.isVariance : true);
            const matchesInvoice =
                !selectedInvoice ||
                (expense.InvoiceNumber || "").toString().toLowerCase().includes(selectedInvoice.toLowerCase());

            return (
                matchesMonth &&
                matchesCategory &&
                matchesSite &&
                matchesSubcategory &&
                matchesVendor &&
                matchesCategorized &&
                matchesVariance &&
                matchesInvoice
            );
        });
    }, [
        groupedByInvoice,
        selectedSYStart,
        selectedMonth,
        selectedCategory,
        selectedSite,
        selectedSubcategory,
        selectedVendor,
        selectedCategorized,
        selectedIsVariance,
        selectedInvoice,
        categoryData,
    ]);

    /* ------------------------- Invoice details ----------------------------- */
    const handleViewInvoiceDetails = useCallback(async (expenseApiId) => {
        try {
            // Get the expense record
            const invoiceResponse = await axios.get(`${API_EXPENSES}?apiid=${expenseApiId}`);
            const invoiceRecord = Array.isArray(invoiceResponse.data)
                ? invoiceResponse.data.find((inv) => inv.apiid === expenseApiId)
                : invoiceResponse.data;

            if (!invoiceRecord) {
                setError("Invoice record not found.");
                return;
            }

            let invoiceItemsData = [];

            // If we have an InvoiceNumber, query AZ_InvoiceItems by InvoiceNumber
            if (invoiceRecord.InvoiceNumber) {
                try {
                    console.log(`Fetching items for Invoice #${invoiceRecord.InvoiceNumber}`);
                    const azItemsResponse = await axios.get(
                        `${API_AZ_INVOICE_ITEMS}?InvoiceNumber=${encodeURIComponent(invoiceRecord.InvoiceNumber)}`
                    );
                    const azItems = Array.isArray(azItemsResponse.data) ? azItemsResponse.data : [];

                    console.log(`Found ${azItems.length} items in AZ_InvoiceItems`);

                    // Map AZ_InvoiceItems fields to display structure
                    invoiceItemsData = azItems.map(item => ({
                        apiid: item.apiid,
                        invoice_apiid: expenseApiId, // Use the expense apiid for reference
                        line_item_name: item.Description || item.ProductCode || 'Unknown Item',
                        quantity: item.Quantity || 0,
                        unit_cost: item.UnitPrice || 0,
                        total_cost: item.Amount || (item.Quantity * item.UnitPrice) || 0,
                        contractprice: item.ContractPrice,
                        // Additional fields for reference
                        ProductCode: item.ProductCode,
                        ManufacturerNumber: item.ManufacturerNumber,
                        Unit: item.Unit,
                        HasVariance: item.HasVariance,
                        VariancePerUnit: item.VariancePerUnit,
                        TotalVarianceAmount: item.TotalVarianceAmount,
                        isNotOnBid: item.isNotOnBid
                    }));
                } catch (err) {
                    console.error("Error fetching from AZ_InvoiceItems:", err);
                }
            }

            // Fallback: Try FINANCE_ExpenseDetails if no items found
            if (invoiceItemsData.length === 0) {
                try {
                    console.log("Trying FINANCE_ExpenseDetails as fallback...");
                    const itemsResponse = await axios.get(`${API_EXPENSE_DETAILS}?invoice_apiid=${expenseApiId}`);
                    invoiceItemsData = Array.isArray(itemsResponse.data)
                        ? itemsResponse.data.filter((item) => item.invoice_apiid === expenseApiId)
                        : [];
                    console.log(`Found ${invoiceItemsData.length} items in FINANCE_ExpenseDetails`);
                } catch (err) {
                    console.log("No items in FINANCE_ExpenseDetails either");
                }
            }

            console.log(`Total items to display: ${invoiceItemsData.length}`);

            setInvoiceDetails(invoiceRecord);
            setInvoiceItems(invoiceItemsData);
            setInvoiceModalOpen(true);
        } catch (error) {
            console.error("Error loading invoice details:", error);
            setError("Failed to load invoice details.");
        }
    }, []);

    const handleEditInvoiceItem = useCallback((item) => {
        setEditingItemId(item.apiid);
        setEditingItemData({
            line_item_name: item.line_item_name,
            quantity: item.quantity,
            unit_cost: item.unit_cost,
        });
    }, []);

    const handleInvoiceItemDataChange = (field, value) =>
        setEditingItemData((prev) => ({ ...prev, [field]: value }));

    const handleSaveInvoiceItem = useCallback(async (itemId) => {
        try {
            await axios.patch(`${API_EXPENSE_DETAILS}/${itemId}`, editingItemData);
            const updatedAmount = Number(editingItemData.quantity) * Number(editingItemData.unit_cost);
            const updatedItems = invoiceItems.map((item) =>
                item.apiid === itemId ? { ...item, ...editingItemData, total_cost: updatedAmount } : item
            );
            setInvoiceItems(updatedItems);
            setEditingItemId(null);
            setEditingItemData({});

            const newTotal = updatedItems.reduce(
                (sum, item) => sum + Number(item.quantity) * Number(item.unit_cost),
                0
            );

            const updatedInvoiceDetails = { ...invoiceDetails, Amount: newTotal };
            setInvoiceDetails(updatedInvoiceDetails);
            await axios.patch(`${API_EXPENSES}/${invoiceDetails.apiid}`, { Amount: newTotal });
        } catch {
            setError("Failed to update invoice item.");
        }
    }, [editingItemData, invoiceItems, invoiceDetails]);

    /* ------------------------- Pricing variance ---------------------------- */
    const openPricingVariance = useCallback(async (row) => {
        try {
            let items = [];

            // Query by InvoiceNumber (primary method)
            if (row.InvoiceNumber) {
                try {
                    console.log(`Fetching variance items for Invoice #${row.InvoiceNumber}`);
                    const { data } = await axios.get(
                        `${API_AZ_INVOICE_ITEMS}?InvoiceNumber=${encodeURIComponent(row.InvoiceNumber)}`
                    );
                    const azItems = Array.isArray(data) ? data : [];
                    console.log(`Found ${azItems.length} items for variance analysis`);

                    items = azItems.map(item => ({
                        apiid: item.apiid,
                        line_item_name: item.Description || item.ProductCode || 'Unknown Item',
                        quantity: item.Quantity,
                        unit_cost: item.UnitPrice,
                        contractprice: item.ContractPrice,
                        // Include additional info for better display
                        sku: item.ProductCode,
                        upc: item.ManufacturerNumber,
                        item_code: item.ProductCode
                    }));
                } catch (err) {
                    console.error("Error fetching variance items:", err);
                }
            }

            // Fallback to FINANCE_ExpenseDetails
            if (items.length === 0) {
                try {
                    console.log("Trying FINANCE_ExpenseDetails for variance...");
                    const { data } = await axios.get(`${API_EXPENSE_DETAILS}?invoice_apiid=${row.apiid}`);
                    items = (Array.isArray(data) ? data : []).filter((i) => i.invoice_apiid === row.apiid);
                    console.log(`Found ${items.length} items in FINANCE_ExpenseDetails`);
                } catch (err) {
                    console.log("No items in FINANCE_ExpenseDetails");
                }
            }

            const computed = items.map((it) => {
                const qty = Number(it.quantity || 0);
                const unit = Number(it.unit_cost || 0);
                const contract = Number(it.contractprice ?? NaN);
                const hasContract = !Number.isNaN(contract);
                const variancePerUnit = hasContract ? unit - contract : 0;
                const varianceTotal = hasContract ? variancePerUnit * qty : 0;
                return {
                    ...it,
                    qty,
                    unit,
                    contract: hasContract ? contract : null,
                    variancePerUnit: hasContract ? variancePerUnit : null,
                    varianceTotal: hasContract ? varianceTotal : null,
                };
            });

            const over = computed
                .filter((c) => c.varianceTotal != null && c.varianceTotal > 0)
                .reduce((s, c) => s + c.varianceTotal, 0);
            const under = computed
                .filter((c) => c.varianceTotal != null && c.varianceTotal < 0)
                .reduce((s, c) => s + c.varianceTotal, 0);
            const net = over + under;

            setVarianceHeader({
                InvoiceNumber: row.InvoiceNumber,
                VendorName: row.VendorName,
                SiteName: row.SiteName,
                Amount: row.Amount,
                ExpenseApiId: row.apiid,
                ImageURL: row.imageURL || "",
            });
            setVarianceItems(computed);
            setVarianceTotals({ over, under, net });
            setVarianceOpen(true);
        } catch (err) {
            console.error("Pricing variance error:", err);
            setError("Failed to load pricing variance.");
        }
    }, []);

    /* ---------- Helper to build pretty HTML email (inline CSS + table) -------- */
    const buildVarianceEmailHTML = ({ header, totals, items }) => {
        const usd = (n) =>
            new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));
        const esc = (s) =>
            (s ?? "")
                .toString()
                .replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

        const rows = items
            .map((it) => {
                const name = esc(it.label);
                const qty = esc(it.qty);
                const contract = it.contract != null ? usd(it.contract) : "—";
                const unit = usd(it.unit);
                const dUnit = it.variancePerUnit != null ? usd(it.variancePerUnit) : "—";
                const dTotNum = it.varianceTotal ?? 0;
                const dTot = it.varianceTotal != null ? usd(dTotNum) : "—";
                const rowClass = dTotNum > 0 ? "over" : dTotNum < 0 ? "under" : "";
                return `
          <tr class="${rowClass}">
            <td class="left">${name}</td>
            <td class="right">${qty}</td>
            <td class="right">${contract}</td>
            <td class="right">${unit}</td>
            <td class="right">${dUnit}</td>
            <td class="right strong">${dTot}</td>
          </tr>`;
            })
            .join("");

        const imageLine = header.ImageURL
            ? `<a href="${esc(header.ImageURL)}" target="_blank" rel="noopener">View Invoice Image</a>`
            : `<span style="opacity:.7">(no image on file)</span>`;

        return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Invoice ${esc(header.InvoiceNumber || "-")} — Price Variance Review</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; padding: 16px; background: #f6f7f9; }
  .card { max-width: 860px; margin: 0 auto; background: #ffffff; border: 1px solid #e6e8eb; border-radius: 8px; padding: 16px 18px; }
  h1 { font-size: 18px; margin: 0 0 10px; }
  .meta { display: grid; grid-template-columns: repeat(2, minmax(240px, 1fr)); gap: 6px 18px; margin: 8px 0 12px; }
  .meta b { font-weight: 700; }
  .badgewrap { margin: 6px 0 14px; }
  .badge { display: inline-block; font-size: 12px; font-weight: 700; padding: 4px 8px; border-radius: 999px; margin-right: 8px; border: 1px solid #e6e8eb; background: #f2f5ff; }
  .badge.over { background: #ffecec; border-color: #ffd2d2; color: #b42318; }
  .badge.under { background: #eafff2; border-color: #c8f5d6; color: #147d3f; }
  .tablewrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  thead th { text-align: right; background: #f6f7fb; border: 1px solid #e6e8eb; padding: 8px; font-weight: 700; white-space: nowrap; }
  thead th.left { text-align: left; }
  tbody td { border: 1px solid #e6e8eb; padding: 8px; vertical-align: top; }
  td.left { text-align: left; }
  td.right { text-align: right; }
  td.strong { font-weight: 700; }
  tbody tr.over td.strong { color: #b42318; }
  tbody tr.under td.strong { color: #147d3f; }
  .muted { color: #556; opacity: .85; }
  .footer { margin-top: 14px; font-size: 12px; color: #667085; }
  .sp { height: 8px; }
  a { color: #1a64db; text-decoration: none; }
  @media (max-width: 520px) { .meta { grid-template-columns: 1fr; } }
</style>
</head>
<body>
  <div class="card">
    <h1>Invoice ${esc(header.InvoiceNumber || "-")} — Price Variance Review</h1>
    <div class="meta">
      <div><b>Vendor:</b> ${esc(header.VendorName || "-")}</div>
      <div><b>Site:</b> ${esc(header.SiteName || "-")}</div>
      <div><b>Invoice Total:</b> ${usd(header.Amount || 0)}</div>
      <div><b>Invoice Image:</b> ${imageLine}</div>
    </div>
    <div class="badgewrap">
      <span class="badge over">Over: ${usd(totals.over)}</span>
      <span class="badge under">Under: ${usd(totals.under)}</span>
      <span class="badge">Net: ${usd(totals.net)}</span>
    </div>
    <div class="tablewrap">
      <table role="table" aria-label="Line Item Variances">
        <thead>
          <tr>
            <th class="left">Item</th>
            <th>Qty</th>
            <th>Contract</th>
            <th>Unit</th>
            <th>Δ / Unit</th>
            <th>Δ Total</th>
          </tr>
        </thead>
        <tbody>
          ${rows || `<tr><td colspan="6" class="left muted">No line-level variances with contract pricing.</td></tr>`}
        </tbody>
      </table>
    </div>
    <div class="footer">
      <div class="sp"></div>
      <div><b>Notes:</b></div>
      <div class="muted">Please reply with approval or corrections. Thank you!</div>
    </div>
  </div>
</body>
</html>`;
    };

    /* -------------------- Email compose (ONE compose; NO downloads) -------------------- */
    const openEmailWithInvoiceLink = useCallback(async () => {
        if (!varianceHeader) return;

        // helpers
        const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
            .format(Number(n || 0));
        const num = (n) => (Number.isFinite(Number(n)) ? String(Number(n)) : "—");
        const clean = (s) => (s || "").toString().replace(/\s+/g, " ").trim();

        // Only mispriced lines (non-zero variance)
        const withVar = (varianceItems || []).filter(i => i.varianceTotal != null && Math.abs(Number(i.varianceTotal)) > 0.0001);
        const overs = withVar.filter(i => i.varianceTotal > 0)
            .sort((a, b) => Math.abs(b.varianceTotal) - Math.abs(a.varianceTotal));
        const unders = withVar.filter(i => i.varianceTotal < 0)
            .sort((a, b) => Math.abs(b.varianceTotal) - Math.abs(a.varianceTotal));
        const ordered = [...overs, ...unders];

        // Build HTML for clipboard (full list)
        const htmlItems = ordered.map(it => {
            const label = clean(
                (it.line_item_name || "-") +
                (it.sku || it.upc || it.item_code ? ` [${it.sku || it.upc || it.item_code}]` : "")
            );
            return {
                label,
                qty: num(it.qty),
                contract: it.contract != null ? Number(it.contract) : null,
                unit: Number(it.unit || 0),
                variancePerUnit: it.variancePerUnit != null ? Number(it.variancePerUnit) : null,
                varianceTotal: it.varianceTotal != null ? Number(it.varianceTotal) : null
            };
        });

        const html = buildVarianceEmailHTML({
            header: varianceHeader,
            totals: varianceTotals,
            items: htmlItems
        });

        // Plain-text (short) for mailto body
        const MAX_LINES = 60;
        const visible = ordered.slice(0, MAX_LINES);
        const hiddenCount = Math.max(ordered.length - visible.length, 0);

        const lines = [
            `Invoice #: ${varianceHeader.InvoiceNumber || "-"}`,
            `Vendor: ${varianceHeader.VendorName || "-"}`,
            `Site: ${varianceHeader.SiteName || "-"}`,
            `Invoice Total: ${usd(varianceHeader.Amount || 0)}`,
            "",
            "Invoice Image Link:",
            varianceHeader.ImageURL ? varianceHeader.ImageURL : "(no image on file)",
            "",
            "Variance Summary (Totals):",
            `• Over:  ${usd(varianceTotals.over)}`,
            `• Under: ${usd(varianceTotals.under)}`,
            `• Net:   ${usd(varianceTotals.net)}`,
            "",
            "Line Item Variances (largest first):",
            "(Item [SKU] — Qty | Contract | Unit | Δ/Unit | Δ Total)"
        ];

        visible.forEach(it => {
            const name = clean(it.line_item_name);
            const sku = clean(it.sku || it.upc || it.item_code || "");
            const qty = num(it.qty);
            const contract = it.contract != null ? usd(it.contract) : "—";
            const unit = usd(it.unit);
            const dUnit = it.variancePerUnit != null ? usd(it.variancePerUnit) : "—";
            const dTot = it.varianceTotal != null ? usd(it.varianceTotal) : "—";
            const label = sku ? `${name} [${sku}]` : name || "-";
            lines.push(`• ${label} — ${qty} | ${contract} | ${unit} | ${dUnit} | ${dTot}`);
        });

        if (hiddenCount > 0) {
            lines.push("", `(+ ${hiddenCount} more line${hiddenCount === 1 ? "" : "s"} omitted for email length)`);
        }

        lines.push(
            "",
            "If the formatted table didn't appear, it's already copied to your clipboard — press Ctrl+V (or Cmd+V) here to paste it."
        );

        const subject = `Invoice ${varianceHeader.InvoiceNumber || "-"} — Price Variance Review (${varianceHeader.VendorName || "Vendor"})`;
        const text = lines.join("\n");

        // Try to put the HTML on the clipboard so the user can paste it in the draft.
        try {
            if (navigator.clipboard && window.ClipboardItem) {
                const htmlBlob = new Blob([html], { type: "text/html" });
                const txtBlob = new Blob([text], { type: "text/plain" });
                await navigator.clipboard.write([new ClipboardItem({ "text/html": htmlBlob, "text/plain": txtBlob })]);
            } else if (navigator.clipboard?.writeText) {
                // Fallback: copy HTML string as text — many clients still paste/convert correctly
                await navigator.clipboard.writeText(html);
            }
        } catch {
            // Ignore clipboard errors — mailto still opens below.
        }

        // Open exactly one compose window (no downloads, no second window)
        const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
        window.location.href = mailto;
    }, [varianceHeader, varianceItems, varianceTotals]);

    /* ---------------------------- Columns (memo) --------------------------- */
    const columns = useMemo(() => {
        const tSx = tooltipSx(theme);
        return [
            {
                field: "actions",
                headerName: "Actions",
                width: 290,
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const id = params.row.apiid;
                    const onFileChange = (e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(id, file);
                    };
                    const hasNotes = notesMap[params.row.apiid]?.length > 0;

                    return (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Tooltip title="Delete Expense" arrow sx={tSx}>
                                <IconButton onClick={() => handleDeleteClick(id)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>

                            {params.row.isInvoice && (
                                <Tooltip title="View Invoice Details" arrow sx={tSx}>
                                    <IconButton onClick={() => handleViewInvoiceDetails(id)} color="success">
                                        <InfoOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {params.row.imageURL ? (
                                <Tooltip title="View Expense Image" arrow sx={tSx}>
                                    <IconButton
                                        onClick={() => {
                                            setSelectedImage(params.row.imageURL);
                                            setImageModalOpen(true);
                                        }}
                                        color="info"
                                    >
                                        <ImageOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        ref={(el) => (fileInputRefs.current[id] = el)}
                                        onChange={onFileChange}
                                    />
                                    <Tooltip title="Upload Receipt Image" arrow sx={tSx}>
                                        <IconButton onClick={() => fileInputRefs.current[id]?.click()} color="primary">
                                            <ImageOutlinedIcon sx={{ transform: "rotate(45deg)" }} />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}

                            <Tooltip
                                title={hasNotes ? "View/Add Notes (has notes)" : "Add Note"}
                                arrow
                                sx={tSx}
                            >
                                <IconButton onClick={() => handleAddNote(params.row.ExpenseID || params.row.apiid)} color="info">
                                    <NoteAddIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Open Pricing Variance" arrow sx={tSx}>
                                <span>
                                    <IconButton
                                        onClick={() => openPricingVariance(params.row)}
                                        disabled={!params.row.isVariance}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            border: `1px solid ${theme.palette.warning.main}`,
                                            borderRadius: "6px",
                                            opacity: params.row.isVariance ? 1 : 0.45,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: 800, lineHeight: 1, color: theme.palette.warning.main }}
                                        >
                                            $
                                        </Typography>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                },
            },
            {
                field: "InvoiceNumber",
                headerName: "Invoice #",
                width: 150,
                editable: true,
                valueGetter: (p) => p.row?.InvoiceNumber ?? "",
                valueSetter: (p) => ({ ...p.row, InvoiceNumber: p.value ?? "" }),
                renderCell: (p) => <Typography>{p.value || "-"}</Typography>,
            },
            {
                field: "SiteName",
                headerName: "Site",
                width: 170,
                editable: true,
                type: "singleSelect",
                valueOptions: () => schoolData.map((s) => s.SchoolName),
                valueSetter: (p) => {
                    const val = p.value;
                    const schoolObj = schoolData.find((s) => s.SchoolName === val);
                    if (schoolObj) {
                        p.row.SiteName = val;
                        p.row.SchoolId = schoolObj.SchoolId;
                    }
                    return { ...p.row };
                },
                renderCell: (p) => <Typography noWrap>{p.value || p.row.SiteName}</Typography>,
            },
            {
                field: "VendorName",
                headerName: "Vendor",
                width: 190,
                editable: true,
                type: "singleSelect",
                valueOptions: () => vendorData.map((v) => v.VendorName),
                valueSetter: (p) => {
                    const v = vendorData.find((x) => x.VendorName === p.value);
                    if (v) {
                        p.row.VendorName = v.VendorName;
                        p.row.VendorID = v.VendorID;
                    }
                    return { ...p.row };
                },
                renderCell: (p) => <Typography noWrap>{p.value || p.row.VendorName}</Typography>,
            },
            {
                field: "ExpenseCategory",
                headerName: "Category",
                width: 170,
                editable: true,
                type: "singleSelect",
                valueOptions: () => categoryData.map((c) => c.CategoryName),
                valueSetter: (p) => {
                    const c = categoryData.find((x) => x.CategoryName === p.value);
                    if (c) {
                        p.row.ExpenseCategory = c.CategoryName;
                        p.row.ExpenseCategoryID = c.CategoryID;
                    }
                    return { ...p.row };
                },
                renderCell: (p) => <Typography noWrap>{p.value || p.row.ExpenseCategory}</Typography>,
            },
            {
                field: "ExpenseSubcategory",
                headerName: "Subcategory",
                width: 190,
                editable: true,
                type: "singleSelect",
                valueOptions: (p) => {
                    const catID = p.row.ExpenseCategoryID;
                    if (!catID) return [];
                    return subcategoryData
                        .filter((sc) => String(sc.CategoryID) === String(catID))
                        .map((sc) => sc.SubcategoryName);
                },
                valueSetter: (p) => {
                    const s = subcategoryData.find((x) => x.SubcategoryName === p.value);
                    if (s) {
                        p.row.ExpenseSubcategory = s.SubcategoryName;
                        p.row.ExpenseSubCategoryID = s.SubcategoryID;
                    }
                    return { ...p.row };
                },
                renderCell: (p) => <Typography noWrap>{p.value || p.row.ExpenseSubcategory}</Typography>,
            },
            {
                field: "ExpenseDate",
                headerName: "Date",
                width: 150,
                editable: true,
                renderEditCell: (p) => <DateEditCell {...p} />,
                valueGetter: (p) => p.row?.ExpenseDate ?? null,
                valueFormatter: ({ value }) =>
                    value
                        ? new Date(value).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                        })
                        : "N/A",
            },
            {
                field: "Amount",
                headerName: "Amount",
                width: 120,
                editable: true,
                align: "right",
                valueFormatter: (p) =>
                    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                        Number(p.value ?? 0)
                    ),
                valueSetter: (p) => ({ ...p.row, Amount: Number(p.value ?? 0) }),
            },
            { field: "Description", headerName: "Description", width: 300, editable: true },
            {
                field: "isCategorized",
                headerName: "Categorized",
                width: 130,
                editable: true,
                type: "boolean",
                renderCell: (p) => (
                    <Checkbox
                        checked={!!p.row.isCategorized}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setExpenseData((prev) =>
                                prev.map((x) => (x.apiid === p.row.apiid ? { ...x, isCategorized: newValue } : x))
                            );
                            fetch(`${API_EXPENSES}/${p.row.apiid}`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Accept: "application/json" },
                                body: JSON.stringify({ isCategorized: newValue }),
                            }).catch(() => setError("Failed to update expense."));
                        }}
                    />
                ),
            },
            {
                field: "ReimbursmentTrackingProgram",
                headerName: "Reimbursement Program",
                width: 210,
                editable: true,
                type: "singleSelect",
                valueOptions: () => [
                    ...new Set(reimbursementPrograms.map((p) => p.program_abbreviation || "NONE")),
                ],
                valueGetter: (p) => p.row.ReimbursmentTrackingProgram ?? "",
                valueSetter: (p) => {
                    const newValue = p.value;
                    const programObj = reimbursementPrograms.find(
                        (r) => (r.program_abbreviation || "NONE") === newValue
                    );
                    if (programObj) {
                        p.row.ReimbursmentTrackingProgram = newValue;
                        p.row.ReimbursementTrackingId = programObj.programid;
                    }
                    return { ...p.row };
                },
                renderCell: (p) => <Typography noWrap>{p.value || ""}</Typography>,
            },
            {
                field: "isVariance",
                headerName: "Price Issue",
                width: 120,
                type: "boolean",
                editable: true,
                renderCell: (p) => (
                    <Tooltip title="Price Issue" arrow sx={tSx}>
                        <Checkbox
                            checked={!!p.row.isVariance}
                            onChange={(e) => handlePriceIssueChange(p.row.apiid, e.target.checked)}
                        />
                    </Tooltip>
                ),
            },
            {
                field: "isPaid",
                headerName: "Paid",
                width: 100,
                type: "boolean",
                editable: true,
                renderCell: (p) => (
                    <Checkbox
                        checked={!!p.row.isPaid}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            handleCellEditCommit({ id: p.id, field: "isPaid", value: newValue });
                            setExpenseData((prev) =>
                                prev.map((x) => (x.apiid === p.id ? { ...x, isPaid: newValue } : x))
                            );
                        }}
                    />
                ),
            },
            {
                field: "PaymentType",
                headerName: "Payment Type",
                width: 140,
                editable: true,
                type: "singleSelect",
                valueOptions: ["", "CC", "Check", "ACH"],
                valueGetter: (p) => p.row.PaymentType ?? "",
                valueSetter: (p) => ({ ...p.row, PaymentType: p.value ?? "" }),
            },
            {
                field: "PaymentInformation",
                headerName: "Payment Information",
                width: 230,
                editable: true,
                valueSetter: (p) => ({ ...p.row, PaymentInformation: p.value ?? "" }),
            },
            {
                field: "PaymentDate",
                headerName: "Payment Date",
                width: 190,
                editable: true,
                renderEditCell: (p) => <DateTimeEditCell {...p} />,
                valueGetter: (p) => p.row?.PaymentDate ?? null,
                valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString("en-US") : "N/A"),
            },
        ];
    }, [
        theme,
        schoolData,
        vendorData,
        categoryData,
        subcategoryData,
        reimbursementPrograms,
        notesMap,
        handleDeleteClick,
        handleImageUpload,
        handleAddNote,
        handlePriceIssueChange,
        handleViewInvoiceDetails,
        openPricingVariance,
        handleCellEditCommit,
    ]);

    /* -------------------------------- Render -------------------------------- */
    return (
        <Box
            m={{ xs: "12px", md: "20px" }}
            sx={{
                backgroundColor: theme.palette.background.default || colors.primary[500],
                color: theme.palette.text.primary || colors.grey[100],
                minHeight: "100vh",
                p: { xs: 1.5, md: 2.5 },
                borderRadius: 2,
                boxShadow: theme.shadows[1],
            }}
        >
            <Header
                title="EXPENSES"
                subtitle="List of Expenses for Financial Management"
                sx={{ color: theme.palette.text.primary || colors.grey[100] }}
            />

            {/* Banner */}
            <Box
                sx={{
                    p: { xs: 1.25, md: 1.75 },
                    mb: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.light
                        })`,
                    color: theme.palette.getContrastText(theme.palette.primary.main),
                    boxShadow: theme.shadows[1],
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    How to use this page
                </Typography>
                <Box
                    component="ul"
                    sx={{
                        m: 0,
                        pl: 2.25,
                        lineHeight: 1.35,
                        listStyle: "disc",
                        columnCount: { xs: 1, md: 2 },
                        columnGap: 3,
                        "& li": { breakInside: "avoid", paddingBottom: 0.25 },
                        "& b": { fontWeight: 700 },
                    }}
                >
                    <li>
                        <b>Filter</b>: School Year, Month, Site, <b>Category</b>, Subcategory, Vendor, Categorized, Price
                        Issue, Invoice #.
                    </li>
                    <li>
                        <b>Add</b>: Click <i>Add New Expense</i>; saving refreshes the grid.
                    </li>
                    <li>
                        <b>Edit</b>: Inline-edit cells. Dates use pickers.
                    </li>
                    <li>
                        <b>Actions</b>: Delete, view/upload receipt image, invoice details, notes, and <b>$</b> pricing variance.
                    </li>
                    <li>
                        <b>Flags</b>: Toggle Paid (sets timestamp), Price Issue, and Categorized.
                    </li>
                </Box>
            </Box>

            {/* Filters */}
            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    p: { xs: 1.25, md: 2 },
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    background:
                        theme.palette.mode === "dark"
                            ? alpha(theme.palette.background.paper, 0.7)
                            : alpha(theme.palette.background.paper, 0.9),
                    "& .MuiInputBase-input, & .MuiInputLabel-root, & .MuiSelect-icon": {
                        color: theme.palette.text.primary,
                    },
                }}
            >
                <Grid container spacing={1.25} alignItems="center">
                    <Grid item xs={6} sm={4} md={3} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>School Year</InputLabel>
                            <Select
                                label="School Year"
                                value={selectedSYStart}
                                onChange={(e) => setSelectedSYStart(Number(e.target.value))}
                            >
                                {availableSYStarts.map((sy) => (
                                    <MenuItem key={sy} value={sy}>
                                        {formatSYLabel(sy)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={3} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Month</InputLabel>
                            <Select
                                label="Filter by Month"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            >
                                <MenuItem value="">All Months</MenuItem>
                                {months.map((m) => (
                                    <MenuItem key={m} value={m}>
                                        {m}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={3} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Site</InputLabel>
                            <Select
                                label="Filter by Site"
                                value={selectedSite}
                                onChange={(e) => setSelectedSite(e.target.value)}
                            >
                                <MenuItem value="">All Sites</MenuItem>
                                {schoolData.map((s) => (
                                    <MenuItem key={s.SchoolId} value={s.SchoolName}>
                                        {s.SchoolName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={3} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Vendor</InputLabel>
                            <Select
                                label="Filter by Vendor"
                                value={selectedVendor}
                                onChange={(e) => setSelectedVendor(e.target.value)}
                            >
                                <MenuItem value="">All Vendors</MenuItem>
                                {vendorsList.map((v) => (
                                    <MenuItem key={v} value={v}>
                                        {v}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={3} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Category</InputLabel>
                            <Select
                                label="Filter by Category"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {categoryData.map((cat) => (
                                    <MenuItem key={cat.CategoryID} value={cat.CategoryID}>
                                        {cat.CategoryName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={3} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Subcategory</InputLabel>
                            <Select
                                label="Filter by Subcategory"
                                value={selectedSubcategory}
                                onChange={(e) => setSelectedSubcategory(e.target.value)}
                            >
                                <MenuItem value="">All Subcategories</MenuItem>
                                {(selectedCategory
                                    ? subcategoryData.filter(
                                        (sc) => String(sc.CategoryID) === String(selectedCategory)
                                    )
                                    : subcategoryData
                                ).map((sc) => (
                                    <MenuItem key={sc.SubcategoryID} value={sc.SubcategoryName}>
                                        {sc.SubcategoryName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={3} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Categorized</InputLabel>
                            <Select
                                label="Filter by Categorized"
                                value={selectedCategorized}
                                onChange={(e) => setSelectedCategorized(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="categorized">Categorized</MenuItem>
                                <MenuItem value="uncategorized">Uncategorized</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={3} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Price Issue</InputLabel>
                            <Select
                                label="Filter by Price Issue"
                                value={selectedIsVariance}
                                onChange={(e) => setSelectedIsVariance(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="review">Needs Review</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={8} sm={6} md={4} lg={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Invoice #"
                            value={invoiceInput}
                            onChange={(e) => setInvoiceInput(e.target.value)}
                            InputLabelProps={{ sx: { color: theme.palette.text.primary } }}
                            inputProps={{ sx: { color: theme.palette.text.primary } }}
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        lg={2.4}
                        sx={{ order: { xs: -1, sm: -1 } }}
                    >
                        <Box display="flex" justifyContent="flex-start">
                            <Button
                                onClick={() => setIsExpenseEntryOpen(true)}
                                variant="contained"
                                sx={{
                                    backgroundColor: '#00CED1',
                                    color: '#000000',
                                    fontWeight: 'bold',
                                    padding: '12px 20px',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    border: '1px solid #00CED1',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    '&:hover': {
                                        backgroundColor: '#20B2AA',
                                        boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
                                    },
                                }}
                            >
                                Add New Expense
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Grid */}
            {loading ? (
                <CircularProgress />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Box height={{ xs: "65vh", md: "75vh" }}>
                    <StyledDataGrid
                        rows={filteredExpenses}
                        columns={columns}
                        getRowId={(row) => row.apiid}
                        disableRowSelectionOnClick
                        density="compact"
                        editMode="row"
                        onCellEditCommit={handleCellEditCommit}
                        columnResizeMode="onChange"
                        rowBuffer={4}
                        columnBuffer={2}
                        disableVirtualization={false}
                        initialState={{
                            pinnedColumns: { left: ["actions"] },
                            sorting: { sortModel: [{ field: "ExpenseDate", sort: "desc" }] },
                            pagination: { paginationModel: { pageSize: 50, page: 0 } },
                        }}
                        pageSizeOptions={[10, 20, 50, 100]}
                        getRowClassName={(params) => {
                            const stripe = params.indexRelativeToCurrentPage % 2 === 0 ? "row--even" : "row--odd";
                            const status = params.row.isVariance ? "varianceRow" : params.row.isPaid ? "paidRow" : "";
                            return [stripe, status].filter(Boolean).join(" ");
                        }}
                    />
                </Box>
            )}

            {/* Modals */}
            <Modal
                open={isExpenseEntryOpen}
                onClose={() => setIsExpenseEntryOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box
                    sx={{
                        width: { xs: "96%", md: "80%" },
                        height: { xs: "88%", md: "80%" },
                        bgcolor: theme.palette.background.paper || colors.primary[500],
                        p: { xs: 1.5, md: 2 },
                        display: "flex",
                        borderRadius: 2,
                        boxShadow: theme.shadows[3],
                        outline: "none",
                    }}
                >
                    <ExpenseEntry
                        onClose={() => setIsExpenseEntryOpen(false)}
                        onSaved={() => {
                            setIsExpenseEntryOpen(false);
                            fetchExpenses();
                        }}
                    />
                </Box>
            </Modal>

            <MediaViewer
                open={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                src={selectedImage}
                type="image"
            />

            <Modal
                open={invoiceModalOpen}
                onClose={() => setInvoiceModalOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box
                    sx={{
                        width: { xs: "96%", sm: "92%", md: "90%" },
                        maxWidth: "900px",
                        bgcolor: theme.palette.background.paper || colors.primary[500],
                        p: { xs: 2, md: 3 },
                        borderRadius: 2,
                        boxShadow: theme.shadows[3],
                        color: theme.palette.text.primary || colors.grey[100],
                        maxHeight: "90vh",
                        overflowY: "auto",
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight={700}>Invoice Details</Typography>
                        <Tooltip title="Close" arrow sx={tooltipSx(theme)}>
                            <IconButton onClick={() => setInvoiceModalOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {invoiceDetails ? (
                        <Box mb={2}>
                            <Typography variant="subtitle1">Invoice Number: {invoiceDetails.InvoiceNumber || "-"}</Typography>
                            <Typography variant="subtitle1">Vendor: {invoiceDetails.VendorName || "-"}</Typography>
                            <Typography variant="subtitle1">
                                Invoice Date: {invoiceDetails.InvoiceDate ? new Date(invoiceDetails.InvoiceDate).toLocaleDateString() : "N/A"}
                            </Typography>
                            <Typography variant="subtitle1">
                                Invoice Total: {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(invoiceDetails.Amount || 0)}
                            </Typography>
                            {coerceBool(invoiceDetails.isVariance) && (
                                <Box mt={1}><Chip label="Has Pricing Issue" color="warning" size="small" variant="filled" /></Box>
                            )}
                        </Box>
                    ) : (
                        <Typography>No invoice details found.</Typography>
                    )}

                    {invoiceItems && invoiceItems.length > 0 ? (
                        <Box mt={2} sx={{ overflowX: "auto" }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Invoice Items</Typography>
                            <table style={{ width: "100%", minWidth: 560, borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        {["Item Name", "Quantity", "Unit Cost", "Total Cost", "Actions"].map((h) => (
                                            <th
                                                key={h}
                                                style={{
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    padding: "8px",
                                                    textAlign: "left",
                                                    background: alpha(theme.palette.action.hover, 0.15),
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceItems.map((item) => (
                                        <tr key={item.apiid}>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px" }}>
                                                {editingItemId === item.apiid ? (
                                                    <input
                                                        type="text"
                                                        value={editingItemData.line_item_name ?? item.line_item_name}
                                                        onChange={(e) => setEditingItemData((p) => ({ ...p, line_item_name: e.target.value }))}
                                                        style={{ padding: "8px", borderRadius: 6, border: `1px solid ${theme.palette.divider}`, width: "100%" }}
                                                    />
                                                ) : (item.line_item_name)}
                                            </td>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px" }}>
                                                {editingItemId === item.apiid ? (
                                                    <input
                                                        type="number"
                                                        value={editingItemData.quantity ?? item.quantity}
                                                        onChange={(e) => setEditingItemData((p) => ({ ...p, quantity: e.target.value }))}
                                                        style={{ padding: "8px", borderRadius: 6, border: `1px solid ${theme.palette.divider}`, width: "100%" }}
                                                    />
                                                ) : (item.quantity)}
                                            </td>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px" }}>
                                                {editingItemId === item.apiid ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={editingItemData.unit_cost ?? item.unit_cost}
                                                        onChange={(e) => setEditingItemData((p) => ({ ...p, unit_cost: e.target.value }))}
                                                        style={{ padding: "8px", borderRadius: 6, border: `1px solid ${theme.palette.divider}`, width: "100%" }}
                                                    />
                                                ) : (
                                                    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(item.unit_cost || 0)
                                                )}
                                            </td>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px" }}>
                                                {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                                                    (Number(editingItemId === item.apiid ? (editingItemData.quantity ?? item.quantity) : item.quantity) *
                                                        Number(editingItemId === item.apiid ? (editingItemData.unit_cost ?? item.unit_cost) : item.unit_cost)) || 0
                                                )}
                                            </td>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px" }}>
                                                {editingItemId === item.apiid ? (
                                                    <Box sx={{ display: "flex", gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => handleSaveInvoiceItem(item.apiid)}
                                                            sx={solidButtonSx(theme)}
                                                            startIcon={<SaveIcon />}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={() => setEditingItemId(null)}
                                                            sx={solidButtonSx(theme)}
                                                            startIcon={<CancelIcon />}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => handleEditInvoiceItem(item)}
                                                        sx={solidButtonSx(theme)}
                                                        startIcon={<EditIcon />}
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    ) : (
                        <Typography>No invoice items found.</Typography>
                    )}

                    <Box mt={2} display="flex" justifyContent="center">
                        <Button variant="contained" color="primary" onClick={() => setInvoiceModalOpen(false)} sx={solidButtonSx(theme)}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={logNotesOpen}
                onClose={() => setLogNotesOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box
                    sx={{
                        width: { xs: "94%", sm: "70%", md: "60%" },
                        bgcolor: theme.palette.background.paper || colors.primary[500],
                        p: { xs: 1.5, md: 2 },
                        borderRadius: 2,
                        boxShadow: theme.shadows[3],
                        outline: "none",
                    }}
                >
                    <LogNotes eventId={selectedEventIdForLog} />
                    <Box mt={2} display="flex" justifyContent="center">
                        <Button variant="contained" color="primary" onClick={() => setLogNotesOpen(false)} sx={solidButtonSx(theme)}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* ---------- Pricing Variance (with solid buttons, responsive) ---------- */}
            <Modal
                open={varianceOpen}
                onClose={() => setVarianceOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box
                    sx={{
                        width: { xs: "96%", sm: "94%", md: "92%" },
                        maxWidth: "1000px",
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        p: { xs: 2, md: 3 },
                        borderRadius: 2,
                        boxShadow: theme.shadows[3],
                        maxHeight: "90vh",
                        overflowY: "auto",
                    }}
                >
                    {/* Top bar with title + solid buttons */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={700}>Pricing Variance</Typography>
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={<NoteAddIcon />}
                                onClick={() => handleAddNote(varianceHeader?.ExpenseApiId)}
                                sx={solidButtonSx(theme)}
                            >
                                Add Note
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<EmailIcon />}
                                onClick={openEmailWithInvoiceLink}
                                sx={solidButtonSx(theme)}
                            >
                                Email w/ Image Link
                            </Button>
                            <IconButton onClick={() => setVarianceOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {varianceHeader && (
                        <Box mb={2} display="grid" gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)" }} gap={1.25}>
                            <Typography><b>Invoice #:</b> {varianceHeader.InvoiceNumber || "-"}</Typography>
                            <Typography><b>Vendor:</b> {varianceHeader.VendorName || "-"}</Typography>
                            <Typography><b>Site:</b> {varianceHeader.SiteName || "-"}</Typography>
                            <Typography>
                                <b>Invoice Total:</b>{" "}
                                {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(varianceHeader.Amount || 0)}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
                        <Chip
                            label={`Over: ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(varianceTotals.over)}`}
                            color="error" variant="filled"
                        />
                        <Chip
                            label={`Under: ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(varianceTotals.under)}`}
                            color="success" variant="filled"
                        />
                        <Chip
                            label={`Net: ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(varianceTotals.net)}`}
                            color={varianceTotals.net > 0 ? "error" : varianceTotals.net < 0 ? "success" : "default"}
                            variant="filled"
                        />
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="contained"
                            size="small"
                            color="secondary"
                            onClick={() => handleAddNote(varianceHeader?.ExpenseApiId)}
                            sx={solidButtonSx(theme)}
                        >
                            Add Note
                        </Button>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    {["Item", "Qty", "Contract $", "Unit $", "Δ / Unit", "Δ Total"].map((h, i) => (
                                        <th
                                            key={h}
                                            style={{
                                                border: `1px solid ${theme.palette.divider}`,
                                                padding: "8px",
                                                textAlign: i === 0 ? "left" : "right",
                                                backgroundColor: alpha(theme.palette.action.hover, 0.18),
                                            }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {varianceItems.map((it) => {
                                    const per = it.variancePerUnit ?? 0;
                                    const total = it.varianceTotal ?? 0;
                                    return (
                                        <tr key={it.apiid} style={{ opacity: it.contract != null ? 1 : 0.6 }}>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px" }}>{it.line_item_name || "-"}</td>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px", textAlign: "right" }}>{it.qty}</td>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px", textAlign: "right" }}>
                                                {it.contract != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(it.contract) : "—"}
                                            </td>
                                            <td style={{ border: `1px solid ${theme.palette.divider}`, padding: "8px", textAlign: "right" }}>
                                                {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(it.unit)}
                                            </td>
                                            <td style={{
                                                border: `1px solid ${theme.palette.divider}`, padding: "8px", textAlign: "right",
                                                color: per > 0 ? theme.palette.error.main : per < 0 ? theme.palette.success.main : "inherit",
                                                fontWeight: per !== 0 ? 700 : 400,
                                            }}>
                                                {it.variancePerUnit != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(per) : "—"}
                                            </td>
                                            <td style={{
                                                border: `1px solid ${theme.palette.divider}`, padding: "8px", textAlign: "right",
                                                color: total > 0 ? theme.palette.error.main : total < 0 ? theme.palette.success.main : "inherit",
                                                fontWeight: total !== 0 ? 700 : 400,
                                            }}>
                                                {it.varianceTotal != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(total) : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {varianceItems.every((i) => i.contract == null) && (
                            <Typography mt={2}>No contract pricing available to compute variance.</Typography>
                        )}
                    </Box>

                    <Box mt={2} display="flex" justifyContent="center">
                        <Button variant="contained" color="primary" onClick={() => setVarianceOpen(false)} sx={solidButtonSx(theme)}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {error && (
                <Typography variant="h5" color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default React.memo(Expenses);